import { Controller } from "@hotwired/stimulus"

// Renders a checkbox per matching weekly date between starts_on / ends_on,
// keyed off weekday. Default-checked = include; unchecking adds the date
// to skip_dates. Holiday dates (from any overlapping semester) are pre-
// skipped + annotated the first time they appear; manual decisions stick
// across input changes, but dates that fall outside the new window are
// dropped (matches the model's normalizer).
//
// The hidden inputs named `recurring_class[skip_dates][]` are the source
// of truth on submit — the visible checkboxes only drive the in-memory
// skipSet. A sentinel empty entry is always rendered so Rails sees the
// param even when nothing is skipped.
export default class extends Controller {
  static targets = [ "startsOn", "endsOn", "weekday", "list", "hiddens", "summary" ]
  static values = {
    holidays:    { type: Object, default: {} as Record<string, string> },
    initialSkip: { type: Array,  default: [] as string[] }
  }

  declare readonly startsOnTarget: HTMLInputElement
  declare readonly endsOnTarget:   HTMLInputElement
  declare readonly weekdayTarget:  HTMLSelectElement
  declare readonly listTarget:     HTMLElement
  declare readonly hiddensTarget:  HTMLElement
  declare readonly summaryTarget:  HTMLElement
  declare readonly holidaysValue:    Record<string, string>
  declare readonly initialSkipValue: string[]

  private skipSet = new Set<string>()
  private seenDates = new Set<string>()

  connect() {
    this.initialSkipValue.forEach(d => this.skipSet.add(d))
    this.render()
  }

  inputChanged() { this.render() }

  toggle(event: Event) {
    const cb = event.target as HTMLInputElement
    const iso = cb.dataset.date!
    if (cb.checked) this.skipSet.delete(iso)
    else            this.skipSet.add(iso)
    this.renderHiddens()
    this.renderSummary(this.currentDates())
  }

  private render() {
    const dates = this.currentDates()
    const isoList = dates.map(d => this.iso(d))

    // Auto-skip holidays on first appearance. Subsequent input changes
    // preserve user decisions even if they re-checked a holiday.
    isoList.forEach(iso => {
      if (this.seenDates.has(iso)) return
      this.seenDates.add(iso)
      if (this.holidaysValue[iso]) this.skipSet.add(iso)
    })

    // Drop skipSet entries that no longer match a candidate date — the
    // model would strip them on save, but the in-memory state should
    // agree with what we render.
    const visible = new Set(isoList)
    for (const d of this.skipSet) if (!visible.has(d)) this.skipSet.delete(d)

    this.listTarget.innerHTML = isoList.length === 0
      ? `<div class="text-sm text-base-content/60 px-1 py-2">Pick a start date, end date, and day of week to see the list of class dates.</div>`
      : isoList.map(iso => this.rowHtml(iso, dates[isoList.indexOf(iso)])).join("")

    this.renderHiddens()
    this.renderSummary(dates)
  }

  private rowHtml(iso: string, date: Date): string {
    const holidayLabel = this.holidaysValue[iso]
    const checked      = !this.skipSet.has(iso)
    const label = date.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric"
    })
    return `
      <label class="flex items-center gap-3 py-1.5 px-1 rounded hover:bg-base-200/60 cursor-pointer">
        <input type="checkbox" class="checkbox checkbox-sm"
               data-date="${iso}"
               data-action="change->recurring-class-dates#toggle"
               ${checked ? "checked" : ""}>
        <span class="text-sm ${holidayLabel ? "text-base-content/70" : "text-base-content"}">${label}</span>
        ${holidayLabel
          ? `<span class="badge badge-warning badge-soft badge-sm">Holiday · ${this.escape(holidayLabel)}</span>`
          : ""}
      </label>
    `
  }

  private renderHiddens() {
    // Sentinel keeps the array param present even when empty.
    const inputs = [`<input type="hidden" name="recurring_class[skip_dates][]" value="">`]
    for (const iso of this.skipSet) {
      inputs.push(`<input type="hidden" name="recurring_class[skip_dates][]" value="${iso}">`)
    }
    this.hiddensTarget.innerHTML = inputs.join("")
  }

  private renderSummary(dates: Date[]) {
    const total = dates.length
    const skipped = dates.filter(d => this.skipSet.has(this.iso(d))).length
    const kept = total - skipped
    this.summaryTarget.textContent = total === 0
      ? ""
      : `${kept} of ${total} dates — ${skipped} skipped`
  }

  private currentDates(): Date[] {
    const start = this.startsOnTarget.value
    const end   = this.endsOnTarget.value
    const weekdayStr = this.weekdayTarget.value
    if (!start || !end || weekdayStr === "") return []
    const weekday = parseInt(weekdayStr, 10)
    if (Number.isNaN(weekday)) return []

    const out: Date[] = []
    // Parse as local date to avoid TZ drift from new Date("YYYY-MM-DD") (UTC).
    const cur = this.parseDate(start)
    const stop = this.parseDate(end)
    if (cur > stop) return []
    while (cur <= stop) {
      if (cur.getDay() === weekday) out.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
    }
    return out
  }

  private parseDate(iso: string): Date {
    const [ y, m, d ] = iso.split("-").map(n => parseInt(n, 10))
    return new Date(y, m - 1, d)
  }

  private iso(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${m}-${d}`
  }

  private escape(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
